import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { ExternalLink, Server, Repeat, Boxes, KeyRound, Clock, CreditCard, Check } from "lucide-react";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export default function MySoftwarePage() {
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
    api.get("/invoices").then(({ data }) => setInvoices(data || [])).catch(() => setInvoices([]));
  }, []);

  const findInvoice = (id) => invoices.find((i) => i.id === id);

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb="My account · Software" title="Your software" subtitle="The platforms you have access to and your subscription details." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {products.map((p) => {
          const a = p.my_allocation || {};
          const url = a.custom_url || p.deployment_url;
          const isPending = a.status === "pending_payment";
          const isPaused = a.status === "paused" || a.status === "cancelled";
          const inv = a.invoice_id ? findInvoice(a.invoice_id) : null;
          const canOpen = !isPending && !isPaused && !!url;

          return (
            <Card key={p.id} className={`overflow-hidden ${isPending ? "ring-1 ring-amber-500/40" : ""}`} data-testid={`software-card-${p.slug || p.id}`}>
              {isPending && (
                <div className="px-5 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-2.5" data-testid="awaiting-payment-banner">
                  <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" strokeWidth={2}/>
                  <div className="flex-1 text-[12.5px] text-amber-800 dark:text-amber-300 leading-snug">
                    <span className="font-semibold">Awaiting payment.</span> Access will open as soon as your invoice clears.
                  </div>
                </div>
              )}
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  {p.icon_url
                    ? <img src={p.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover ring-1 ring-[hsl(var(--border))]"/>
                    : <div className="w-12 h-12 rounded-lg bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] grid place-items-center font-bold text-lg">{(p.name || "?").slice(0, 2).toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{p.name}</h3>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400">
                          <KeyRound className="w-2.5 h-2.5" strokeWidth={2.2}/>Awaiting payment
                        </span>
                      ) : (
                        <StatusPill variant={a.status === "active" ? "success" : a.status === "trial" ? "primary" : "default"}>{a.status || "active"}</StatusPill>
                      )}
                    </div>
                    {p.description && <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5 line-clamp-2">{p.description}</div>}
                  </div>
                </div>

                {/* Action area */}
                {canOpen ? (
                  <div className="space-y-1.5">
                    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(var(--primary-hover))] transition-colors" data-testid={`open-${p.slug || p.id}`}>
                      <Server className="w-3.5 h-3.5" strokeWidth={1.6}/>Open {p.name}<ExternalLink className="w-3 h-3 ml-auto" strokeWidth={1.6}/>
                    </a>
                    <div className="text-[10px] text-[hsl(var(--text-muted))] text-center">Single sign-on coming soon · for now you may need to log in separately</div>
                  </div>
                ) : isPending ? (
                  <div className="space-y-2">
                    {inv?.hosted_invoice_url ? (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 text-white text-[13px] font-semibold hover:bg-amber-600 transition-colors" data-testid={`pay-now-${p.slug || p.id}`}>
                        <CreditCard className="w-3.5 h-3.5" strokeWidth={1.8}/>Pay {inv.number ? `${inv.number} · ` : ""}{gbp(inv.amount || 0)}<ExternalLink className="w-3 h-3 ml-auto" strokeWidth={1.6}/>
                      </a>
                    ) : (
                      <div className="px-3 py-2 rounded-lg bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] text-[12px] text-center" data-testid="no-invoice-yet">
                        <Clock className="inline w-3 h-3 mr-1" strokeWidth={1.8}/>Your account manager will send an invoice shortly.
                      </div>
                    )}
                    <div className="opacity-60 cursor-not-allowed flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] text-[13px] font-semibold" data-testid={`open-disabled-${p.slug || p.id}`}>
                      <Server className="w-3.5 h-3.5" strokeWidth={1.6}/>Open {p.name}
                      <KeyRound className="w-3 h-3 ml-auto" strokeWidth={1.8}/>
                    </div>
                  </div>
                ) : url ? (
                  <div className="opacity-60 cursor-not-allowed flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] text-[13px] font-semibold">
                    <Server className="w-3.5 h-3.5" strokeWidth={1.6}/>Open {p.name} <span className="ml-auto text-[10px] uppercase tracking-widest">{a.status}</span>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{isPending ? "Setup due" : "Setup paid"}</div>
                    <div className="font-mono text-[15px] font-semibold mt-1">{gbp((a.setup_fee || 0) * 1.2)}</div>
                    <div className="text-[10px] text-[hsl(var(--text-subtle))] mt-0.5">inc 20% VAT · {gbp(a.setup_fee || 0)} ex VAT</div>
                  </div>
                  <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3">
                    <div className={`text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1 ${isPending ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--primary))]"}`}><Repeat className="w-3 h-3" strokeWidth={2}/>Monthly</div>
                    <div className="font-mono text-[15px] font-semibold mt-1">{gbp((a.monthly_fee || 0) * 1.2)}<span className="text-[11px] text-[hsl(var(--text-muted))] font-normal">/mo</span></div>
                    <div className="text-[10px] text-[hsl(var(--text-subtle))] mt-0.5">inc 20% VAT</div>
                  </div>
                </div>

                {a.activated_at ? (
                  <div className="text-[11px] text-emerald-700 dark:text-emerald-400 uppercase tracking-widest font-semibold inline-flex items-center gap-1">
                    <Check className="w-3 h-3" strokeWidth={2.5}/>Active since {new Date(a.activated_at).toLocaleDateString()}
                  </div>
                ) : isPending ? (
                  <div className="text-[11px] text-amber-700 dark:text-amber-400 uppercase tracking-widest font-semibold">Allocated {a.started_at ? new Date(a.started_at).toLocaleDateString() : ""} · awaiting funds</div>
                ) : a.started_at ? (
                  <div className="text-[11px] text-[hsl(var(--text-muted))] uppercase tracking-widest font-semibold">Allocated {new Date(a.started_at).toLocaleDateString()}</div>
                ) : null}
              </div>
            </Card>
          );
        })}
        {products.length === 0 && <div className="lg:col-span-2"><Empty title="No software allocated yet" subtitle="Once we allocate a SaaS subscription for you it will appear here." icon={Boxes} /></div>}
      </div>
    </div>
  );
}
